/**
 * CarePlanList — table view of care plans for a participant.
 *
 * Phase 3 (Care Plan v2) extraction. Renders the legacy table that lived
 * inline inside ParticipantDetailPage.js. The component is purely presentational —
 * the parent CarePlanTab owns the data + action handlers. When `care_plan_v2_enabled`
 * is ON, an additional "Acknowledge" action is exposed alongside the existing
 * Submit / Approve / PDF actions; the diff link is shown when the plan has a
 * previous_version_id.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/shared/StatusBadge';

export function CarePlanList({
  plans,
  onAction,
  onExportPdf,
  onOpenDetail,
  onOpenAck,
  onOpenDiff,
  v2Enabled,
  extraActions = [],
}) {
  return (
    <Table data-testid="care-plan-list-table">
      <TableHeader>
        <TableRow>
          <TableHead>Plan No</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Version</TableHead>
          <TableHead>Status</TableHead>
          {v2Enabled && <TableHead>Last ack</TableHead>}
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.length === 0 ? (
          <TableRow>
            <TableCell colSpan={v2Enabled ? 6 : 5} className="text-center py-4 text-muted-foreground">
              No care plans
            </TableCell>
          </TableRow>
        ) : plans.map(p => (
          <TableRow key={p.id} data-testid={`care-plan-row-${p.id}`}>
            <TableCell className="font-mono text-xs">
              <button
                type="button"
                className="hover:underline text-primary"
                onClick={() => onOpenDetail?.(p)}
                data-testid={`care-plan-open-${p.id}`}
              >
                {p.plan_no}
              </button>
            </TableCell>
            <TableCell className="font-medium">{p.title}</TableCell>
            <TableCell>
              {p.version}
              {v2Enabled && p.previous_version_id && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => onOpenDiff?.(p)}
                  className="ml-1 h-6 px-1 text-[10px]"
                  data-testid={`care-plan-diff-${p.id}`}
                >
                  diff
                </Button>
              )}
            </TableCell>
            <TableCell>
              <StatusBadge status={p.status} />
              {/* BS3-012: staff only see APPROVED plans */}
              {['DRAFT', 'REVIEW'].includes(String(p.status || '').toUpperCase()) && (
                <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`care-plan-staff-hidden-${p.id}`}>
                  Not visible to staff until approved
                </div>
              )}
            </TableCell>
            {v2Enabled && (
              <TableCell className="text-xs text-muted-foreground">
                {p.last_acknowledged_at ? String(p.last_acknowledged_at).slice(0, 10) : '—'}
              </TableCell>
            )}
            <TableCell>
              <div className="flex gap-1">
                {p.status === 'DRAFT' && (
                  <Button size="sm" variant="ghost"
                          onClick={() => onAction(p.id, 'submit-review')}
                          data-testid={`care-plan-submit-${p.id}`}>Submit</Button>
                )}
                {p.status === 'REVIEW' && (
                  <Button size="sm" variant="ghost"
                          onClick={() => onAction(p.id, 'approve')}
                          data-testid={`care-plan-approve-${p.id}`}>Approve</Button>
                )}
                {v2Enabled && p.status === 'APPROVED' && (
                  <Button size="sm" variant="ghost"
                          onClick={() => onOpenAck?.(p)}
                          data-testid={`care-plan-ack-${p.id}`}>Acknowledge</Button>
                )}
                <Button size="sm" variant="ghost"
                        onClick={() => onExportPdf(p.id)}
                        data-testid={`care-plan-pdf-${p.id}`}>PDF</Button>
                {extraActions.map((a, i) => (
                  <Button key={i} size="sm" variant="ghost"
                          onClick={() => a.onClick(p)}
                          data-testid={a.testId ? `${a.testId}-${p.id}` : undefined}>
                    {a.icon}{a.label}
                  </Button>
                ))}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
